/**
 * Avatar Component
 * ----------------
 * A flexible avatar component built on top of Radix Avatar + CVA.
 * Supports image, text, and fallback icon use-cases with size, shape,
 * and color-scheme variants.
 *
 * Core Concepts
 * -------------
 * 1. `type` controls WHAT is rendered
 *    - `image` → renders image if `src` is provided, otherwise fallback icon
 *    - `text`  → renders initials or short text
 *
 * 2. `variant` controls SHAPE
 *    - `round`  → circular avatar (default)
 *    - `square` → squared avatar (used for logos / org avatars)
 *
 * 3. `size` controls container + internal icon scaling (Only for type==='round')
 *    - xs | sm | md | lg | xl
 *
 * 4. `colorScheme` controls background & icon/text color
 *    - default | brand | blue_cyan | caramel | red | purple | green | yellow | lime | teal | pink
 *
 * Usage Examples
 * --------------
 *
 * Image Avatar (default behavior)
 * <Avatar
 *   src="/user.png"
 *   alt="User profile"
 * />
 *
 * Image Avatar with fallback icon
 * <Avatar
 *   type="image"
 *   size="md"
 * />
 *
 * Square Logo Avatar
 * <Avatar
 *   type="image"
 *   variant="square"
 *   colorScheme="brand"
 * />
 *
 * Text Avatar (Initials)
 * <Avatar
 *   type="text"
 *   text="FA"
 *   size="sm"
 *   colorScheme="purple"
 * />
 *
 * Custom Fallback Icon
 * <Avatar
 *   type="image"
 *   FallbackImage={MyCustomIcon}
 * />
 *
 * Notes
 * -----
 * - If `type="image"` and `src` is missing, fallback icon is shown automatically
 * - Square avatars always use a fixed internal icon size
 * - Text avatars ignore `src`
 * - Color behavior differs slightly for `square + image` vs `square + text`
 */

import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { ReactComponent as ProfileImg } from 'src/assets/tailwind/user-03.svg';
import { cn } from 'src/utils/utils';

import { Icon } from './icon';

// Segmentic Token of card avater are not define. As it is same for both dark and light mode, So we are using the direct color variables

const avatarVariants = cva(
  'tw:relative tw:inline-flex tw:shrink-0 tw:overflow-hidden tw:rounded-full tw:items-center tw:justify-center',
  {
    variants: {
      type: {
        image: 'tw:border-none',
        text: 'tw:font-medium',
        file: ''
      },
      variant: {
        round: 'tw:border-1 tw:border-border-primary ',
        square: 'tw:rounded-l tw:border-0'
      },
      colorScheme: {
        default:
          'tw:bg-[var(--Color-Primary-25)] tw:text-[var(--Color-Primary-500)]',
        brand: 'tw:bg-[var(--Color-Primary-25)]',
        blue_cyan: 'tw:bg-[var(--Color-Info-100)]',
        caramel: 'tw:bg-[var(--Color-Warning-100)]',
        red: 'tw:bg-[var(--Color-Error-100)]',
        purple: 'tw:bg-[var(--Others-Purple-100)]',
        green: 'tw:bg-[var(--Color-Success-100)]',
        yellow: 'tw:bg-[var(--Others-Yellow-100)]',
        lime: 'tw:bg-[var(--Others-Lime-100)]',
        teal: 'tw:bg-[var(--Others-Teal-100)]',
        pink: 'tw:bg-[var(--Others-Pink-100)]',
        none: ''
      },
      size: {
        xs: 'tw:w-xl tw:h-xl tw:text-sm',
        sm: 'tw:w-2xl tw:h-2xl tw:text-md',
        md: 'tw:w-3xl tw:h-3xl tw:text-lg',
        lg: 'tw:w-4xl tw:h-4xl tw:text-xl',
        xl: 'tw:w-6xl tw:h-6xl tw:text-2xl'
      }
    },
    compoundVariants: [
      {
        type: 'file',
        className: 'tw:rounded-none'
      },
      {
        variant: 'square',
        className: 'tw:!w-4xl tw:!h-4xl tw:text-2xl tw:font-2xl'
      },
      {
        variant: 'round',
        type: 'text',
        colorScheme: 'default',
        className: 'tw:bg-secondary tw:text-text-secondary'
      },
      {
        variant: 'round',
        type: 'image',
        colorScheme: 'default',
        className: 'tw:bg-secondary tw:text-icon-primary'
      }
    ],
    defaultVariants: {
      type: 'image',
      variant: 'round',
      colorScheme: 'default',
      size: 'lg'
    }
  }
);

const SIZE_MAP: Record<string, string> = {
  xs: 'tw:w-m tw:h-m',
  sm: 'tw:w-l tw:h-l',
  md: 'tw:w-xl tw:h-xl',
  lg: 'tw:w-2xl tw:h-2xl',
  xl: 'tw:w-3xl tw:h-3xl'
};

/** The `colorScheme` values this accepts. Return type for callers that compute one. */
export type AvatarColorScheme = NonNullable<
  VariantProps<typeof avatarVariants>['colorScheme']
>;

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof avatarVariants> {
  src?: string | React.ComponentType<React.SVGProps<SVGSVGElement>>;
  alt?: string;
  fallbackImage?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  subIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  text?: string;
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      variant,
      colorScheme,
      type,
      size,
      src,
      alt,
      fallbackImage: FallbackImage,
      subIcon: SubIcon,
      text,
      ...props
    },
    ref
  ) => {
    const showImage = (type === 'image' || type === 'file') && src;
    let showFallback = type != 'text' && !src;
    const showText = type === 'text';
    const DefaultFallBackIcon = ProfileImg;

    if (!type) {
      showFallback = true;
    }

    const imageSizeClasses =
      variant === 'round'
        ? SIZE_MAP[size as string] || SIZE_MAP.md
        : type === 'file'
        ? 'tw:w-3xl tw:h-3xl'
        : 'tw:w-2xl tw:h-2xl';

    const getColorScheme = () => {
      if (variant === 'round' && type === 'text') {
        switch (colorScheme) {
          case 'brand':
            return 'tw:text-[var(--Color-Primary-600)] tw:border-[var(--Color-Primary-500)]';
          case 'blue_cyan':
            return 'tw:text-[var(--Color-Info-600)] tw:border-[var(--Color-Info-500)]';
          case 'caramel':
            return 'tw:text-[var(--Color-Warning-600)] tw:border-[var(--Color-Warning-500)]';
          case 'red':
            return 'tw:text-[var(--Color-Error-600)] tw:border-[var(--Color-Error-500)]';
          case 'purple':
            return 'tw:text-[var(--Others-Purple-600)] tw:border-[var(--Others-Purple-500)]';
          case 'green':
            return 'tw:text-[var(--Color-Success-600)] tw:border-[var(--Color-Success-500)]';
          case 'yellow':
            return 'tw:text-[var(--Others-Yellow-600)] tw:border-[var(--Others-Yellow-500)]';
          case 'lime':
            return 'tw:text-[var(--Others-Lime-600)] tw:border-[var(--Others-Lime-500)]';
          case 'teal':
            return 'tw:text-[var(--Others-Teal-600)] tw:border-[var(--Others-Teal-500)]';
          case 'pink':
            return 'tw:text-[var(--Others-Pink-600)] tw:border-[var(--Others-Pink-500)]';
          case 'default':
            return '';
          default:
            return 'tw:text-[var(--Color-Primary-600)] tw:border-[var(--Color-Primary-500)]';
        }
      } else if (variant === 'square' && type === 'text') {
        switch (colorScheme) {
          case 'brand':
            return 'tw:text-[var(--Color-Primary-600)]';
          case 'blue_cyan':
            return 'tw:text-[var(--Color-Info-600)]';
          case 'caramel':
            return 'tw:text-[var(--Color-Warning-600)]';
          case 'red':
            return 'tw:text-[var(--Color-Error-600)]';
          case 'purple':
            return 'tw:text-[var(--Others-Purple-600)]';
          case 'green':
            return 'tw:text-[var(--Color-Success-600)]';
          case 'yellow':
            return 'tw:text-[var(--Others-Yellow-600)]';
          case 'lime':
            return 'tw:text-[var(--Others-Lime-600)]';
          case 'teal':
            return 'tw:text-[var(--Others-Teal-600)]';
          case 'pink':
            return 'tw:text-[var(--Others-Pink-600)]';
          case 'default':
            return '';
          default:
            return 'tw:text-[var(--Color-Primary-600)]';
        }
      } else if (type === 'image') {
        switch (colorScheme) {
          case 'brand':
            return 'tw:text-[var(--Color-Primary-500)]';
          case 'blue_cyan':
            return 'tw:text-[var(--Color-Info-500)]';
          case 'caramel':
            return 'tw:text-[var(--Color-Warning-500)]';
          case 'red':
            return 'tw:text-[var(--Color-Error-500)]';
          case 'purple':
            return 'tw:text-[var(--Others-Purple-500)]';
          case 'green':
            return 'tw:text-[var(--Color-Success-500)]';
          case 'yellow':
            return 'tw:text-[var(--Others-Yellow-500)]';
          case 'lime':
            return 'tw:text-[var(--Others-Lime-500)]';
          case 'teal':
            return 'tw:text-[var(--Others-Teal-500)]';
          case 'pink':
            return 'tw:text-[var(--Others-Pink-500)]';
          case 'default':
            return '';
          default:
            return '';
        }
      }
      return '';
    };
    const IsSrcComponent = typeof src === 'function';
    return (
      <div className="tw:relative tw:inline-flex tw:shrink-0">
        <AvatarPrimitive.Root
          ref={ref}
          className={cn(
            avatarVariants({ type, variant, colorScheme, size }),
            getColorScheme(),
            className
          )}
          {...props}
        >
          {showImage ? (
            <>
              {IsSrcComponent ? (
                <div
                  className={cn(
                    imageSizeClasses,
                    'tw:flex tw:items-center tw:justify-center'
                  )}
                >
                  {React.createElement(
                    src as React.ComponentType<React.SVGProps<SVGSVGElement>>,
                    {
                      className: 'tw:w-full tw:h-full'
                    }
                  )}
                </div>
              ) : (
                <AvatarPrimitive.Image
                  src={src as string}
                  alt={alt}
                  className="tw:h-full tw:w-full tw:object-cover"
                />
              )}
            </>
          ) : null}
          {showFallback ? (
            FallbackImage ? (
              <div className={cn(imageSizeClasses)}>
                <FallbackImage className="tw:w-full tw:h-full" />
              </div>
            ) : (
              <div className={cn(imageSizeClasses)}>
                <DefaultFallBackIcon className="tw:w-full tw:h-full" />
              </div>
            )
          ) : null}
          {showText ? <span>{text}</span> : null}
        </AvatarPrimitive.Root>
        {SubIcon ? (
          <div
            className={cn(
              'tw:absolute tw:bottom-0 tw:right-0 tw:translate-x-1/4 tw:translate-y-1/4 tw:z-10',
              'tw:rounded-xs tw:bg-primary tw:p-3xs tw:shadow-e0',
              'tw:flex tw:items-center tw:justify-center',
              'tw:w-m tw:h-m'
            )}
          >
            <Icon icon={SubIcon} size={12} className="tw:shrink-0" />
          </div>
        ) : null}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';
export default Avatar;
